import * as THREE from 'three';
import { HUD, HUD_TAGS } from '../../config/tuning';
import type { Projector, ScreenPoint } from '../../render/Camera';
import { createScreenPoint } from '../../render/Camera';
import type { HudPainter } from '../HudLayout';
import type { HudDegradation } from '../HudDegradation';
import { TagKind, type BearingTag, type HudFrame } from '../HudFrame';
import type { HorizonGeometry } from './Horizon';

/**
 * Bearing tags (SPEC §3.4.1): short label pairs anchored to the horizon at the object's true
 * azimuth — designation on the upper line, type on the lower. In the reference these read
 * `K-2 / FIRE POINT`.
 *
 * The anchor is built by projecting a point at the object's *azimuth and zero elevation*, rather
 * than the object itself. That is what makes a tag sit on the horizon bar regardless of whether
 * the site is in a valley or on a ridge, and it keeps a distant tag from sliding up and down the
 * display as the terrain under it changes.
 *
 * Off-screen tags clamp to the screen edge with a directional caret, so a threat behind the
 * aircraft still tells you which way to look.
 */

const anchorDirection = new THREE.Vector3();
const projected: ScreenPoint = createScreenPoint();

/**
 * One tag's resolved screen placement.
 *
 * Pooled at module scope and reused every frame: the HUD redraws at the display rate and must not
 * allocate (CLAUDE.md), and a per-frame array of these would be the largest allocation on the
 * instrument.
 */
interface Placement {
  tag: BearingTag;
  x: number;
  y: number;
  halfWidthPx: number;
  /** Vertical space this tag needs, pixels — a target carries a third line for its range. */
  heightPx: number;
  offScreen: boolean;
  priority: number;
  /** Formatted range, or '' when this tag does not show one. */
  rangeText: string;
  /** The rounded range the text was built from, so it is only rebuilt when the digits change. */
  rangeKey: number;
}

const placements: Placement[] = [];
const order: number[] = [];

/** Grow the pool on demand. Happens once per mission at most, never in steady state. */
function placementAt(index: number): Placement {
  let placement = placements[index];
  if (placement === undefined) {
    placement = {
      tag: EMPTY_TAG,
      x: 0,
      y: 0,
      halfWidthPx: 0,
      heightPx: 0,
      offScreen: false,
      priority: 0,
      rangeText: '',
      rangeKey: Number.NaN,
    };
    placements[index] = placement;
  }
  return placement;
}

/**
 * Range from the aircraft to a target, as it reads on the tag: `850M` under a kilometre, `10.1KM`
 * above it.
 *
 * Metres below the threshold because the last kilometre is the one where the number is being used
 * to *aim* rather than to navigate, and "0.8KM" is a worse answer there than "850M". One decimal
 * above, because a target ten kilometres out that reported whole kilometres would sit on the same
 * reading for forty seconds.
 */
export function rangeText(metres: number): string {
  const clamped = Math.max(0, metres);
  if (clamped < HUD_TAGS.rangeKmThresholdM) return `${Math.round(clamped)}M`;
  return `${(clamped / 1000).toFixed(1)}KM`;
}

/**
 * The quantised value `rangeText` depends on — metres, or tenths of a kilometre.
 *
 * Comparing this instead of the string is what keeps the range out of the render loop's
 * allocation budget: a tag whose displayed digits have not moved reuses the string it already has.
 */
function rangeKeyOf(metres: number): number {
  const clamped = Math.max(0, metres);
  return clamped < HUD_TAGS.rangeKmThresholdM
    ? Math.round(clamped)
    : HUD_TAGS.rangeKmThresholdM + Math.round(clamped / 100);
}

const EMPTY_TAG: BearingTag = {
  designation: '',
  type: '',
  positionM: new THREE.Vector3(),
  kind: TagKind.Waypoint,
};

/**
 * Which tags keep their natural position when two want the same patch of screen.
 *
 * A target and a threat are the two things a player is steering by or away from; a waypoint is
 * navigation furniture. So the furniture moves.
 */
function tagPriority(kind: BearingTag['kind']): number {
  if (kind === TagKind.Target) return 0;
  if (kind === TagKind.Threat) return 1;
  return 2;
}

/** Screen y where the horizon line crosses a given x. */
export function horizonYAt(
  horizon: HorizonGeometry,
  x: number,
  centreX: number,
  centreY: number,
): number {
  const cos = Math.cos(horizon.rollRad);
  if (Math.abs(cos) < 1e-4) return centreY;
  return centreY + (horizon.offsetPx + (x - centreX) * Math.sin(horizon.rollRad)) / cos;
}

export function drawBearingTags(
  painter: HudPainter,
  frame: HudFrame,
  projector: Projector,
  aircraftPositionM: THREE.Vector3,
  degradation: HudDegradation,
  horizon: HorizonGeometry,
): void {
  const layout = painter.layout;
  const ctx = painter.ctx;
  const margin = layout.px(HUD.marginPx);
  const lineHeight = layout.fontPx * 1.35;
  const caretSize = layout.px(HUD.missileCaretPx) * 0.6;
  const stackStep = lineHeight * 3.2;

  // Tags are held out of the readout columns. The airspeed and altitude numbers live at the
  // screen edges at mid-height, which is exactly where a clamped tag would otherwise land — and
  // a threat label overprinting the altitude is worse than either of them being absent.
  const edgeInset = margin * HUD.tagEdgeInsetFactor;
  const minX = edgeInset;
  const maxX = layout.widthPx - edgeInset;
  // Vertical band: clear of the heading tape above and of the RWR and status cluster below.
  const minY = margin * 2.5;
  const maxY = layout.heightPx - margin * 4.5;

  // --- Pass 1: where does each tag want to be?
  //
  // Resolved for every tag before any is drawn, because a collision is a property of the *set*
  // and cannot be seen one tag at a time. The previous version only stacked tags that had been
  // clamped to a screen edge, so anything sharing an on-screen bearing simply overprinted — and
  // a route runs toward its target, so most of the interesting tags share a bearing.
  let count = 0;
  for (let i = 0; i < frame.bearingTags.length; i += 1) {
    const tag = frame.bearingTags[i];
    if (degradation.tagDropped(i)) continue;

    const azimuth = tagAzimuthRad(tag, aircraftPositionM) + degradation.tagDriftRad(i);
    // A direction at the tag's azimuth and zero elevation, so it lands on the horizon line
    // whatever the camera is doing.
    anchorDirection.set(Math.sin(azimuth), 0, -Math.cos(azimuth));

    const inFront = projector.projectDirection(anchorDirection, projected);
    const wantedX = inFront ? projected.x : projected.x > layout.centreX ? maxX : minX;
    const clampedX = clamp(wantedX, minX, maxX);
    const offScreen = !inFront || clampedX !== projected.x;

    const placement = placementAt(count);
    count += 1;
    placement.tag = tag;
    placement.x = clampedX;
    placement.y = inFront
      ? projected.y
      : horizonYAt(horizon, clampedX, layout.centreX, layout.centreY);
    placement.offScreen = offScreen;
    placement.priority = tagPriority(tag.kind);

    // Range, on targets only. Taken from the tag's *displayed* position, which already carries the
    // GPS error, so the number agrees with the mark the player is looking at rather than with a
    // truth the navigation system has lost (SPEC §3.5.4).
    if (tag.kind === TagKind.Target && tag.destroyed !== true) {
      const east = tag.positionM.x - aircraftPositionM.x;
      const north = tag.positionM.z - aircraftPositionM.z;
      const rangeM = Math.hypot(east, north);
      const key = rangeKeyOf(rangeM);
      if (key !== placement.rangeKey) {
        placement.rangeKey = key;
        placement.rangeText = rangeText(rangeM);
      }
    } else {
      placement.rangeText = '';
      placement.rangeKey = Number.NaN;
    }

    // Monospace, so a character count is an exact width — no measureText, no font state to set.
    const characters = Math.max(
      tag.designation.length,
      tag.type.length,
      placement.rangeText.length,
    );
    placement.halfWidthPx = characters * layout.fontPx * HUD_TAGS.charWidthFactor * 0.5;
    placement.heightPx = placement.rangeText === '' ? stackStep : stackStep + lineHeight;
  }

  // --- Pass 2: resolve collisions by pushing the lower-priority tag down.
  //
  // Priority order first so that a target or a threat keeps the position that means something,
  // and navigation furniture is what moves. Ties break on x, which keeps the layout stable frame
  // to frame rather than flickering as tags swap order.
  order.length = 0;
  for (let i = 0; i < count; i += 1) order.push(i);
  order.sort((a, b) => {
    const byPriority = placements[a].priority - placements[b].priority;
    return byPriority !== 0 ? byPriority : placements[a].x - placements[b].x;
  });

  for (let n = 0; n < order.length; n += 1) {
    const placement = placements[order[n]];
    // Push down past anything already placed that this would overlap. Bounded by the number of
    // tags, so a pathological case degrades to a neat column rather than looping.
    for (let guard = 0; guard < order.length; guard += 1) {
      let moved = false;
      for (let m = 0; m < n; m += 1) {
        const other = placements[order[m]];
        const horizontalGap = Math.abs(placement.x - other.x);
        if (horizontalGap >= placement.halfWidthPx + other.halfWidthPx + margin * 0.25) continue;
        // Separation is set by the taller of the two: a three-line target tag needs more room
        // beneath it than a two-line waypoint, and using one step for both let the range line
        // print into whatever was stacked below.
        const step = Math.max(placement.heightPx, other.heightPx);
        const verticalGap = Math.abs(placement.y - other.y);
        if (verticalGap >= step) continue;
        placement.y = other.y + step;
        moved = true;
      }
      if (!moved) break;
    }
    placement.y = clamp(placement.y, minY, maxY);
  }

  // --- Pass 3: draw, in the original tag order so colour and z-order stay predictable.
  for (let i = 0; i < count; i += 1) {
    const placement = placements[i];
    const tag = placement.tag;
    const colour = tag.kind === TagKind.Target ? HUD.designationColour : undefined;

    ctx.save();
    ctx.translate(placement.x, placement.y);

    // Stalk down to the labels, so the text does not sit on the horizon line itself.
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(0, lineHeight * 0.6);
    painter.stroke(1, colour);

    painter.text(tag.designation, 0, lineHeight * 1.4, 'centre', colour);
    painter.text(tag.type, 0, lineHeight * 2.4, 'centre', colour);
    if (placement.rangeText !== '') {
      painter.text(placement.rangeText, 0, lineHeight * 3.4, 'centre', colour);
    }

    if (placement.offScreen) {
      const pointingRight = placement.x >= maxX;
      const direction = pointingRight ? 1 : -1;
      ctx.beginPath();
      ctx.moveTo(direction * caretSize, 0);
      ctx.lineTo(0, -caretSize * 0.55);
      ctx.lineTo(0, caretSize * 0.55);
      ctx.closePath();
      painter.stroke(1, colour);
    }

    ctx.restore();
  }
}

/** Azimuth of a tag from the aircraft, radians clockwise from north. */
export function tagAzimuthRad(tag: BearingTag, aircraftPositionM: THREE.Vector3): number {
  const east = tag.positionM.x - aircraftPositionM.x;
  const north = -(tag.positionM.z - aircraftPositionM.z);
  return Math.atan2(east, north);
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}
